import { Component, OnInit, ViewChild } from '@angular/core';
import { Log } from "ng2-logger";
import { ConfirmationModalComponent } from '@app/shared/components';
import { TableConfig } from "@app/shared/util/table-config.utils";
import { TranslateService } from "@ngx-translate/core";

import { ToolReturn } from "@models/tool-return.model";
import { ToolReturnsService } from "@services/tool-returns.service";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Subscription } from "rxjs";
import { NotificationService } from "@services/notification.service";
import { Section } from "@models/enums/section.utils";
import { PendingToolReturnsModalComponent } from "@app/shared/pages/pending-tool-returns-modal/pending-tool-returns-modal.component";
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-pending-tool-returns',
    templateUrl: './pending-tool-returns.component.html',
    styleUrls: ['./pending-tool-returns.component.scss']
})
export class PendingToolReturnsComponent implements OnInit {
    _logger = Log.create(PendingToolReturnsComponent.name);

    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;
    @ViewChild(PendingToolReturnsModalComponent, {static: true}) pendingToolReturnsModal: PendingToolReturnsModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;

    pendingToolReturns: ToolReturn[] = [];
    tableConfig = new TableConfig();
    isLoading = false;
    toolReturnQueueSubscription: Subscription;

    confirmationPopupMessage: string;

    filterStatusList = [];
    isConfirmationInProgress = false;
    showFilters = true;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(private toolReturnsService: ToolReturnsService,
                private translationService: TranslateService,
                private notificationService: NotificationService) {

        this.filterStatusList = this.buildStatusFilterColumnKeys();

        this.translationService.onLangChange.subscribe((event) => {
            this.filterStatusList = this.buildStatusFilterColumnKeys();
        });

    }

    ngOnInit() {
        this.getPendingToolReturns(this.tableConfig);
        this.toolReturnQueueSubscription = this.notificationService.lineSetterToolReturnQueueAsObservable.subscribe((value) => {
            if (value) {
                this._logger.info("ngOnInit:: toolReturnQueueSubscription got value", value);
                this.getPendingToolReturns(this.tableConfig);
            }
        });
    }

    getPendingToolReturns(tableConfig = this.tableConfig) {
        this._logger.info('getPendingToolReturns:: Retrieving pending tool returns ');
        this.isLoading = true;
        this.toolReturnsService.getAllToolReturns(tableConfig, 'LINE', Section.ViewToolReturns).subscribe(
            (data) => {
                this._logger.info('getPendingToolReturns:: API Returned data', data);
                this.pendingToolReturns = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            (error) => {
                this._logger.error('getPendingToolReturns:: API Returned error', error);
                this.isLoading = false;
            }
        )
    }

    onClose(toolReturn: ToolReturn) {
        this.cancelToolReturnRequest(toolReturn);
    }

    onTableRowClick(toolReturn: ToolReturn) {
        this.toolReturnsService.getToolReturnRequestDetails(toolReturn).subscribe(
            (data) => {
                this._logger.info('onTableRowClick:: API Returned data', data);
                this.pendingToolReturnsModal.open(data);
            },
            (error) => {
                this._logger.info('onTableRowClick:: API Returned error', error);
            }
        );
    }

    buildCancelMessage(pendingToolReturnRequestName) {
        return this.translationService.instant('lineSetter.pending-tool-returns.table.toasts.closed',
            {pendingToolReturnRequestName: pendingToolReturnRequestName});
    }

    onSortChange(tableState) {
        this.getPendingToolReturns(tableState);
    }

    onPageChange(tableState) {
        this.getPendingToolReturns(tableState);
    }

    onFilter(tableConfig: TableConfig) {
        this.getPendingToolReturns(tableConfig);
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translationService.instant("generic.statuses.all"), value: 'All'},
            {
                name: this.translationService.instant("generic.statuses.new"),
                value: ToolRequestStatus.NEW
            },
            {
                name: this.translationService.instant("generic.statuses.inTransport"),
                value: ToolRequestStatus.IN_TRANSPORT
            },
            {
                name: this.translationService.instant("generic.statuses.approved"),
                value: ToolRequestStatus.APPROVED
            },
            {
                name: this.translationService.instant("generic.statuses.rejected"),
                value: ToolRequestStatus.REJECTED
            },
            {
                name: this.translationService.instant("generic.statuses.pending"),
                value: ToolRequestStatus.WAITING_FOR_APPROVAL
            },
            {
                name: this.translationService.instant("generic.statuses.closed"),
                value: ToolRequestStatus.CLOSED
            },
            {
                name: this.translationService.instant("generic.statuses.canceled"),
                value: ToolRequestStatus.CANCELED
            }
        ];
    }

    cancelToolReturnRequest(selectedToolReturn) {
        this.confirmationPopupMessage = '';
        this._logger.info('onClose:: Closing Tool Return request', selectedToolReturn);
        this.isConfirmationInProgress = true;
        this.toolReturnsService.cancelToolReturnRequest(selectedToolReturn).subscribe(
            (data) => {
                this._logger.info('onClose:: API Returned data', data);
                this.getPendingToolReturns();
                this.confirmationPopupMessage = this.buildCancelMessage(selectedToolReturn.toolRequest.name);
                this.pendingToolReturnsModal.closeConfirmationModal();
                this.isConfirmationInProgress = false;
                this.logoutModal.open();
            },
            (error) => {
                this.isConfirmationInProgress = false;
                this.pendingToolReturnsModal.closeConfirmationModal();
                if (error && error.error) {
                    if (error.error.message !== 'errorMessage.401.invalidBoschId') {
                        this.confirmationPopupMessage = this.translationService.instant(error.error.message);
                        this.errorModal.open();
                    }
                }
                this._logger.error('onClose:: API Returned error', error);
            }
        )
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }

}
